// Shared mail types.

export type MailLabel = { id: number; name: string; color: string };

export type MailRule = {
  id: number;
  name: string;
  enabled: boolean;
  sort_order: number;
  match_all: boolean;
  from_contains: string | null;
  to_contains: string | null;
  subject_contains: string | null;
  body_contains: string | null;
  move_to_folder: string | null;
  add_label_id: number | null;
  mark_read: boolean;
  mark_important: boolean;
  mark_starred: boolean;
};

export type MailContact = {
  id: number;
  name: string;
  email: string;
  organization: string | null;
  title: string | null;
  phone: string | null;
  memo: string | null;
  /** 마지막으로 이 사람에게 메일 보낸 시각(보낸 적 없으면 null). */
  last_sent_at?: string | null;
};

export type MailFolder = {
  id: number;
  name: string;
  code: string;     // "CF<id>" — value stored in Mail.folder
  sort_order: number;
  /** 상위 메일함 id. null 이면 최상위. 서버는 늘 주는데 타입에서 빠져 있어
   *  메뉴가 계층을 모른 채 평면으로 나열됐다(부모·자식이 뒤섞여 보임). */
  parent_id: number | null;
  total: number;
  unread: number;
};

/**
 * 메일함을 화면에 뿌릴 순서로 펼친다(부모 바로 아래에 그 자식들).
 *
 * 서버는 같은 레벨 안에서의 sort_order 만 준다. 그래서 그대로 나열하면
 * "명철 개인(0) · 내부 운영(1) · 서대문구(1·프로젝트의 자식) …" 처럼
 * 부모와 자식이 번갈아 섞인다. 트리를 따라 내려가며 순서를 만들어야 한다.
 */
export function flattenFolders(
  folders: MailFolder[],
): { folder: MailFolder; depth: number }[] {
  const out: { folder: MailFolder; depth: number }[] = [];
  const walk = (parentId: number | null, depth: number) => {
    folders
      .filter((f) => (f.parent_id ?? null) === parentId)
      .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
      .forEach((f) => {
        out.push({ folder: f, depth });
        walk(f.id, depth + 1);
      });
  };
  walk(null, 0);
  // 부모가 지워졌는데 남아 있는 자식이 있으면 빠뜨리지 않고 뒤에 붙인다.
  if (out.length < folders.length) {
    const seen = new Set(out.map((x) => x.folder.id));
    folders.filter((f) => !seen.has(f.id)).forEach((f) => out.push({ folder: f, depth: 0 }));
  }
  return out;
}

export type MailAttachment = {
  /** 본문 <img src="cid:..."> 와 이어 주는 값(인라인 이미지). */
  content_id?: string | null;
  id: number;
  file_name: string;
  file_size: number;
  mime_type: string | null;
};

export type Mail = {
  id: number;
  folder: string;
  from_addr: string;
  to_addrs: string[];
  cc_addrs: string[];
  subject: string | null;
  is_read: boolean;
  is_starred: boolean;
  is_important: boolean;
  has_attachments: boolean;
  size_bytes: number;
  /** 외부 발송이 실패한 경우의 사유(성공이면 null). */
  delivery_error?: string | null;
  received_at: string | null;
  sent_at: string | null;
  scheduled_at: string | null;
  individual?: boolean;
  created_at: string;
  thread_id: string | null;
  in_reply_to_id: number | null;
  preview: string | null;
  labels: MailLabel[];
};

export type MailDetail = Mail & {
  body_html: string | null;
  body_text: string | null;
  bcc_addrs: string[];
  attachments: MailAttachment[];
};

export type FolderCounts = {
  RECEIVED: number;
  UNREAD: number;
  INBOX: number;
  ADS: number;
  SENT: number;
  DRAFT: number;
  TRASH: number;
  SPAM: number;
  STARRED: number;
  IMPORTANT: number;
  SCHEDULED: number;
  unread_inbox: number;
  unread?: Record<string, number>;
};

export type Signature = {
  body_html: string | null;
  body_text: string | null;
  auto_attach: boolean;
};

export const FOLDER_LABELS: Record<string, string> = {
  RECEIVED: "전체 메일함",
  UNREAD: "안 읽은 메일",
  INBOX: "받은 메일함",
  ADS: "광고 메일함",
  STARRED: "별표 표시",
  IMPORTANT: "중요 메일",
  SENT: "보낸 메일함",
  DRAFT: "임시 보관함",
  SCHEDULED: "예약 발송",
  SPAM: "스팸함",
  TRASH: "휴지통",
};

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function fmtDate(s: string | null | undefined): string {
  if (!s) return "";
  const d = new Date(s);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  }
  // 요일까지 보여 준다("8월 28일 (금)"). ko-KR 은 weekday 를 괄호로 묶어 날짜 뒤에 붙인다.
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString("ko-KR", { month: "short", day: "numeric", weekday: "short" })
    : d.toLocaleDateString("ko-KR", { year: "numeric", month: "short", day: "numeric", weekday: "short" });
}

export function fmtFullDate(s: string | null | undefined): string {
  if (!s) return "";
  const d = new Date(s);
  // 날짜와 시각을 따로 만들어 요일을 **날짜 바로 뒤**에 넣는다("2026. 8. 28. (금) 오후 3:24:00").
  // toLocaleString 에 weekday 를 얹으면 로케일이 순서를 정해 시각 뒤로 밀릴 수 있다.
  const day = d.toLocaleDateString("ko-KR");
  const dow = d.toLocaleDateString("ko-KR", { weekday: "short" });
  return `${day} (${dow}) ${d.toLocaleTimeString("ko-KR")}`;
}

/** HTML 본문을 읽을 수 있는 텍스트로 바꾼다.
 *
 * 외부에서 오는 메일의 절반가량은 HTML 만 있고 text 파트가 없다. 그대로 두면
 * 답장·전달 시 인용문이 통째로 비어 버린다. DOMParser 로 파싱해 textContent 만
 * 꺼내므로 스크립트가 실행되지 않는다(innerHTML 로 붙이는 것과 다름). */
export function htmlToText(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("script, style, head").forEach((el) => el.remove());
    // 줄바꿈이 되는 태그는 실제 줄바꿈으로 바꿔 준다 — 안 그러면 한 줄로 뭉친다.
    doc.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
    doc.querySelectorAll("p, div, tr, li, h1, h2, h3, h4, blockquote").forEach((el) => {
      el.append("\n");
    });
    // 링크 주소를 글자로 남긴다.
    //
    // 예전에는 태그를 버리면서 href 도 함께 사라져, 전달·답장하면 "PC에 저장" 같은
    // 글자만 남고 정작 눌러야 할 주소가 없어졌다. 관공서·대기업은 큰 파일을 메일에
    // 싣지 않고 내려받기 주소로 보내므로(2026-09-22 korea.kr 25MB 건), 그 주소가
    // 사라지면 전달받은 사람은 파일에 닿을 방법이 없다.
    doc.querySelectorAll("a[href]").forEach((el) => {
      const href = (el.getAttribute("href") || "").trim();
      if (!/^https?:\/\//i.test(href)) return;          // mailto:·내부 앵커는 그대로 둔다
      const label = (el.textContent || "").trim();
      if (label.includes(href)) return;                  // 이미 주소가 보이면 중복으로 붙이지 않는다
      el.append(` (${href})`);
    });
    const text = doc.body?.textContent ?? "";
    return text
      .replace(/\u00a0/g, " ")      // &nbsp;
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")   // 빈 줄이 과하게 늘어나는 것 방지
      .trim();
  } catch {
    return "";
  }
}

/** text/plain 안에 남아 있는 HTML 엔티티(&nbsp; &amp; ...)를 실제 글자로 되돌린다.
 *
 * 일부 그룹웨어는 평문 파트를 만들 때 태그만 걷어내고 엔티티는 그대로 둔다. 그러면
 * 인용문에 "&nbsp;" 가 글자 그대로 박힌다(실제로 sdm.go.kr 발신 메일이 그랬다).
 * 우리가 만든 게 아니어도, 인용할 때 풀어 주면 읽을 수 있는 글이 된다. */
function decodeEntities(text: string): string {
  if (!/&(nbsp|amp|lt|gt|quot|#\d+);/i.test(text)) return text;
  try {
    const doc = new DOMParser().parseFromString(text, "text/html");
    return (doc.body?.textContent ?? text).replace(/\u00a0/g, " ");
  } catch { return text; }
}

/** 서명 한 덩어리를 HTML 로. 작성 화면(서식 편집기)과 발송용 HTML 이 같은 모양이어야
 *  해서 한 곳에 둔다 — 따로 만들면 화면과 실제 보낸 메일이 달라진다.
 *
 *  구분선은 서명 글자를 담은 div 바깥의 <hr> 다. div 에 border-top 을 걸면 편집기에서
 *  Enter 를 칠 때마다 스타일째 복제돼 가로줄이 늘어난다(실제로 그런 적이 있다). */
export function signatureHtml(sig: string): string {
  return `<div><br></div>`
    + `<hr style="margin:12px 0;border:none;border-top:1px solid #e5e7eb">`
    + `<div style="font-size:12.5px;line-height:1.6;color:#6b7280">${toHtmlLines(sig)}</div>`;
}

/** 답장·전달에 붙일 원본 인용문. 본문이 HTML 뿐이어도 내용이 남도록 한다. */
export function quoteOriginal(m: {
  from_addr: string; to_addrs?: string[]; subject?: string | null;
  body_text?: string | null; body_html?: string | null;
  received_at?: string | null; sent_at?: string | null; created_at: string;
}, kind: "reply" | "forward" = "reply"): string {
  const when = new Date(m.received_at || m.sent_at || m.created_at).toLocaleString("ko-KR");
  const text = decodeEntities((m.body_text || "").trim()) || htmlToText(m.body_html || "");
  const head = kind === "forward"
    ? `--------- 전달된 메일 ---------\n보낸사람: ${m.from_addr}\n받는사람: ${(m.to_addrs || []).join(", ")}\n날짜: ${when}\n제목: ${m.subject || ""}`
    : `--------- 원본 메일 ---------\n보낸사람: ${m.from_addr}\n날짜: ${when}\n제목: ${m.subject || ""}`;
  // 인용 표시(>)를 붙여 내가 쓴 글과 원본이 구분되게 한다.
  const quoted = text ? text.split("\n").map((l) => `> ${l}`).join("\n") : "> (본문 없음)";
  return `\n\n${head}\n\n${quoted}\n`;
}

/** 서명 구분선. RFC 표준(`-- `)을 쓰면 받는 쪽 메일 앱이 서명으로 인식해 접어 준다. */
export const SIG_SEPARATOR = "\n-- \n";

/** 인용문 시작 표시 — 발송 시 HTML 로 바꿀 때 어디부터가 원본인지 찾는 데 쓴다. */
export const QUOTE_HEADS = ["--------- 원본 메일 ---------", "--------- 전달된 메일 ---------"];

/** 본문을 '내가 쓴 글 / 인용한 원본 / 서명'으로 나눈다. AI 첨삭이 내 글만 다루고
 *  인용문·서명은 손대지 않도록, renderMailHtml 과 같은 규칙(서명 먼저, 그 다음
 *  인용문)으로 자른다. quote·sig 는 구분 표식을 포함한 원문 그대로라 이어 붙이면
 *  원래 본문이 복원된다. */
export function splitMailText(raw: string): { mine: string; quote: string; sig: string } {
  let rest = raw || "";
  let sig = "";
  const si = rest.lastIndexOf(SIG_SEPARATOR);
  if (si >= 0) { sig = rest.slice(si); rest = rest.slice(0, si); }
  let mine = rest;
  let quote = "";
  for (const head of QUOTE_HEADS) {
    const i = rest.indexOf(head);
    if (i >= 0) { mine = rest.slice(0, i); quote = rest.slice(i); break; }
  }
  return { mine, quote, sig };
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** 줄바꿈을 살려 HTML 문단으로. 사용자가 쓴 글이므로 반드시 이스케이프한다. */
function toHtmlLines(text: string): string {
  const escaped = esc(text.trimEnd());
  // 평문 속 주소를 누를 수 있게 만든다. 전달·답장의 인용문에 들어간 내려받기 주소가
  // 글자로만 남으면 받는 사람이 복사해서 붙여야 한다.
  const linked = escaped.replace(/(https?:\/\/[^\s<>"']+)/g, (hit) => {
    const tail = (hit.match(/[.,;:)\]]+$/) || [""])[0];   // 문장 끝 기호는 주소에서 뺀다
    const url = tail ? hit.slice(0, hit.length - tail.length) : hit;
    return `<a href="${url}" style="color:#2563eb">${url}</a>${tail}`;
  });
  return linked.split("\n").join("<br>");
}

/**
 * 작성한 평문을 보기 좋은 HTML 로 만든다.
 *
 * 평문만 보내면 받는 쪽에서 서명과 인용문이 본문과 구분되지 않아 지저분하다.
 * 본문 / 서명 / 인용문 세 덩어리로 나눠 각각 다르게 그린다:
 *   · 본문   — 그대로
 *   · 서명   — 위에 옅은 구분선, 작고 흐린 글씨
 *   · 인용문 — 왼쪽 세로줄(blockquote), 흐린 글씨
 */
export function renderMailHtml(text: string): string {
  const raw = text || "";

  // 1) 서명 분리 — 서명은 **맨 아래**(인용한 원본보다도 뒤)에 온다.
  //    그래서 인용문보다 먼저 떼어 내야 한다. 인용문을 먼저 자르면 서명이 원본
  //    안에 들어간 것으로 오해해 상대방 글처럼 인용 표시가 붙는다.
  let rest = raw;
  let sigPart = "";
  const si = raw.lastIndexOf(SIG_SEPARATOR);
  if (si >= 0) {
    rest = raw.slice(0, si);
    sigPart = raw.slice(si + SIG_SEPARATOR.length);
  }

  // 2) 인용문 분리
  let bodyPart = rest;
  let quoted = "";
  // **가장 먼저 나오는** 표시에서 자른다. 배열 순서대로 찾으면 안 된다 —
  // 전달 메일에는 '전달된 메일'과 '원본 메일'이 함께 들어 있는데, 배열에서 '원본'을
  // 먼저 보는 바람에 맨 끝에서 잘렸다. 그러면 앞쪽 인용문 전체가 '본문'으로 넘어가
  // 화면에 '> ' 가 그대로 노출된다(실제로 그렇게 보였다).
  let cut = -1;
  for (const head of QUOTE_HEADS) {
    const i = rest.indexOf(head);
    if (i >= 0 && (cut < 0 || i < cut)) cut = i;
  }
  if (cut >= 0) {
    bodyPart = rest.slice(0, cut);
    quoted = rest.slice(cut);
  }

  const parts: string[] = [
    `<div style="font-family:-apple-system,'Segoe UI','Malgun Gothic',sans-serif;font-size:14px;line-height:1.7;color:#1f2937">`,
    // 본문이 비어도 `<br>` 을 남긴다. 빈 `<div></div>` 는 높이가 0 이라 편집기에서
    // 클릭할 자리가 없고, 그러면 커서가 아래 서명 안으로 들어가 거기서 글을 쓰게 된다.
    `<div>${toHtmlLines(bodyPart) || "<br>"}</div>`,
  ];

  // 순서: 쓴 글 → 인용한 원본 → 서명. 서명이 가장 아래에 오게 그린다.
  if (quoted.trim()) {
    // '> ' 와 '--------- 원본 메일 ---------' 은 평문 규약이라 실제로 나가는 본문에는
    // 남겨야 하지만, 화면에 그대로 두면 지저분하다. 왼쪽 세로줄과 들여쓰기로 대신
    // 보여 주고, 머리글은 작은 캡션으로 만든다. 중첩 인용('> > ')도 한 번에 걷어낸다.
    const qlines = quoted.split("\n");
    const caption = (qlines[0] || "").replace(/-+/g, "").trim() || "원본 메일";
    const inner = qlines.slice(1).join("\n").replace(/^(>\s?)+/gm, "");
    parts.push(
      `<div style="margin:22px 0 0;padding:2px 0 2px 14px;border-left:3px solid #e5e7eb">` +
      `<div style="font-size:11.5px;font-weight:700;color:#9ca3af;letter-spacing:.04em;` +
      `margin:0 0 7px">${esc(caption)}</div>` +
      `<div style="color:#6b7280;font-size:13px;line-height:1.7">${toHtmlLines(inner)}</div>` +
      `</div>`,
    );
  }

  if (sigPart.trim()) {
    // 구분선은 **서명 글자를 담은 div 바깥**, 독립된 <hr> 로 그린다.
    //
    // 예전에는 서명 div 자체에 border-top 을 걸었다. 그런데 편집기(contenteditable)
    // 에서 그 안에 커서를 두고 Enter 를 치면 브라우저가 그 div 를 통째로 복제한다 —
    // 즉 **줄을 바꿀 때마다 구분선이 하나씩 더 생긴다**. 실제로 작성 화면이 가로줄로
    // 도배됐다. <hr> 은 빈 요소라 복제되지 않으므로 Enter 를 아무리 쳐도 선은 하나다.
    //
    // 서명 위 빈 줄은 커서를 놓고 글을 쓸 자리다(서명 안에 쓰게 되던 문제).
    parts.push(signatureHtml(sigPart));
  }

  parts.push(`</div>`);
  return parts.join("");
}

/** 보낸 메일의 수신자별 수신 확인 상태 */
export type MailReceipt = {
  recipient: string;
  kind: "internal" | "external";
  status: "read" | "unread" | "opened" | "unknown" | "bounced" | "deleted";
  at: string | null;
  count: number;
  shared: boolean;   // 한 통으로 여러 명에게 보내 누가 열었는지 구분되지 않음
};
export type ReceiptSummary = { total: number; read: number };
